/**
 * The upload pipeline: one file, driven from the browser through the media-v3
 * flow — init (or resume from a stored record) → PUT every part with bounded
 * retry → final. Processing status is NOT polled: the hook subscribes to the
 * realtime relay's `attachment.processed` event on the attachment's channel
 * (`_lib/processing-announcements.ts`) and folds the terminal outcome in when
 * media announces it.
 *
 * The pipeline never touches React. It emits `UploadEvent`s that the hook folds
 * into job state with `applyUploadEvent` (a pure calculation), keeping the
 * pipeline decoupled from the view. Resume progress is persisted through the
 * resume-store so an interrupted upload can be finished by re-picking the file.
 */

import { anySignal } from "@/libs/any-signal";
import {
  ApiError,
  computePartCount,
  finalUpload,
  initUploads,
  type MediaClientConfig,
  type MediaUploadAccess,
  PART_CONCURRENCY,
  PART_MAX_ATTEMPTS,
  PART_SIZE_BYTES,
  postTranscriptDraft,
  putPartWithRetry,
  UploadCancelledError,
} from "../_api/media-v3-client";
import type {
  InitItem,
  MediaUploadJob,
  MediaUploadPhase,
  StatusItem,
} from "../_types/media-upload";
import {
  clearResumeRecord,
  fingerprintFile,
  markPartDone,
  type ResumeRecord,
  saveResumeRecord,
} from "./resume-store";

/** Progress signals the pipeline emits; the hook folds them into job state. */
export type UploadEvent =
  | { type: "phase"; phase: MediaUploadPhase }
  | {
      type: "init";
      attachmentId: string;
      contentUri: string;
      access: MediaUploadAccess;
    }
  | {
      type: "resumed";
      attachmentId: string;
      contentUri: string;
      /** The stored init's route pair — what media announces this attachment by. */
      access: MediaUploadAccess;
      partsDone: number;
      skipped: number;
      note: string;
    }
  | { type: "progress"; partsDone: number }
  | { type: "bytes"; bytesSent: number }
  | { type: "retry"; note: string }
  | { type: "status"; item: StatusItem }
  | { type: "final"; contentUri: string | null; thumbnailUri: string | null }
  | { type: "analysis-ready"; text: string; model: string | null }
  | { type: "analysis-failed"; reason: string }
  | { type: "analysis-none" }
  | { type: "note"; note: string }
  | { type: "draft"; stored: boolean }
  | { type: "error"; error: string };

/**
 * Fold one event into the job. Pure — no I/O, no React.
 *
 * The three `analysis-*` arms settle {@link MediaUploadJob.analysis}. `ready`
 * always wins — the text is the valuable thing and it is only ever announced
 * once — while `failed`/`none` settle a still-`pending` analysis only, so a
 * late "nothing to analyze" (a `/uploads/status` reconcile, which cannot carry
 * text) can never erase a transcript that already arrived.
 */
export function applyUploadEvent(
  job: MediaUploadJob,
  event: UploadEvent,
): MediaUploadJob {
  switch (event.type) {
    case "phase":
      return { ...job, phase: event.phase };
    case "init":
      return {
        ...job,
        phase: "uploading",
        attachmentId: event.attachmentId,
        contentUri: event.contentUri,
        access: event.access,
      };
    case "resumed":
      return {
        ...job,
        phase: "uploading",
        attachmentId: event.attachmentId,
        contentUri: event.contentUri,
        access: event.access,
        partsDone: event.partsDone,
        bytesSent: event.partsDone * PART_SIZE_BYTES,
        resumedSkipped: event.skipped,
        note: event.note,
      };
    case "progress":
      return { ...job, partsDone: event.partsDone };
    case "bytes":
      return { ...job, bytesSent: event.bytesSent };
    case "retry":
      return { ...job, retries: job.retries + 1, note: event.note };
    case "status":
      return {
        ...job,
        status: event.item.status,
        contentUri: event.item.contentUri ?? job.contentUri,
        thumbnailUri: event.item.thumbnailUri ?? job.thumbnailUri,
        aiAnalyzed: event.item.aiAnalyzed,
        // The processed object's own type/size — a HEIC lands as image/webp.
        contentType: event.item.contentType ?? job.contentType,
        size: event.item.fileSize ?? job.size,
      };
    case "final":
      // A plain file is announced processed inside the `/final` request, so the
      // realtime event can beat the response. It is the newer news: keep it.
      if (job.phase === "done" || job.phase === "failed") return job;
      return {
        ...job,
        phase: "processing",
        contentUri: event.contentUri ?? job.contentUri,
        thumbnailUri: event.thumbnailUri ?? job.thumbnailUri,
      };
    case "analysis-ready":
      return {
        ...job,
        analysis: { status: "ready", text: event.text, model: event.model },
      };
    case "analysis-failed":
      if (job.analysis.status !== "pending") return job;
      return { ...job, analysis: { status: "failed", reason: event.reason } };
    case "analysis-none":
      if (job.analysis.status !== "pending") return job;
      return { ...job, analysis: { status: "none" } };
    case "note":
      return { ...job, note: event.note };
    case "draft":
      return { ...job, draftStored: event.stored };
    case "error":
      return { ...job, phase: "failed", error: event.error };
  }
}

export interface UploadDeps {
  /** Origin of the media service. */
  baseUrl: string;
  /** Fetch a fresh bearer token — called once per authed request. */
  getToken: () => Promise<string | null>;
  /** Fired after the localStorage resume store changes, so the UI can refresh. */
  onStoreChange: () => void;
}

export interface RunUploadArgs {
  file: File;
  deps: UploadDeps;
  /** A matching fresh resume record to finish, or null/undefined to start fresh. */
  resumeRecord?: ResumeRecord | null;
  onEvent: (event: UploadEvent) => void;
  /**
   * Text the browser already holds for this file (live voice captions). Posted as the
   * attachment's transcript DRAFT right after `/init` and BEFORE any byte goes up, so it
   * is in the database by the time `/final` dispatches processing — which then skips the
   * server-side transcription pass instead of racing it. A failed post is reported (the
   * `draft` event) and never blocks the upload.
   */
  transcriptDraft?: { text: string; model: string };
  /**
   * Cancels the upload: in-flight PUTs abort, nothing further is sent, the resume
   * record is dropped (the person changed their mind — a re-pick starts fresh), and
   * the job ends `failed` with the cancellation as its error.
   */
  signal?: AbortSignal;
  /** Route pair to use — `public` is the signed-out vendor-onboarding path. Default `authed`. */
  access?: MediaUploadAccess;
  /** `false` asks `/final` to skip the AI analysis. Default: analyze. */
  runAiAnalysis?: boolean;
}

function throwIfCancelled(signal: AbortSignal | undefined): void {
  if (signal?.aborted) throw new UploadCancelledError();
}

/** How often at most the byte counter is announced while parts stream (per file). */
const BYTES_EVENT_INTERVAL_MS = 150;

/**
 * Sums the in-flight parts' byte counters into one `bytes` event, rate-limited so
 * a fast uplink firing XHR progress every few ms doesn't re-render per tick.
 * `flush()` announces immediately — used at part boundaries so the last bytes of
 * a part are never left un-announced behind the throttle.
 */
function createByteReporter(
  doneBytes: number,
  onEvent: (event: UploadEvent) => void,
) {
  const inFlight = new Map<number, number>();
  let settled = doneBytes;
  let lastAt = 0;
  const total = () =>
    settled + [...inFlight.values()].reduce((sum, n) => sum + n, 0);
  const announce = () => {
    lastAt = Date.now();
    onEvent({ type: "bytes", bytesSent: total() });
  };
  return {
    report(partNumber: number, loaded: number) {
      inFlight.set(partNumber, loaded);
      if (Date.now() - lastAt >= BYTES_EVENT_INTERVAL_MS) announce();
    },
    settle(partNumber: number, size: number) {
      inFlight.delete(partNumber);
      settled += size;
      announce();
    },
  };
}

/**
 * Build a client config with a freshly-fetched token (parts take time). The
 * public route pair never carries one, so a signed-out caller is not even asked.
 */
async function authConfig(
  deps: UploadDeps,
  access: MediaUploadAccess,
): Promise<MediaClientConfig> {
  if (access === "public") return { baseUrl: deps.baseUrl, token: "" };
  const token = await deps.getToken();
  return { baseUrl: deps.baseUrl, token: token ?? "" };
}

/**
 * Run one file end to end. Never throws — every failure is surfaced as an
 * `error` event so the caller can keep other files going.
 */
export async function runUpload({
  file,
  deps,
  resumeRecord,
  onEvent,
  signal,
  transcriptDraft,
  access = "authed",
  runAiAnalysis,
}: RunUploadArgs): Promise<void> {
  const fingerprint = fingerprintFile(file, access);
  try {
    throwIfCancelled(signal);
    let init: InitItem;
    const etagByPart = new Map<number, string>();
    const doneParts = new Set<number>();

    // ── init, or resume from a stored record (reusing its 72h init URLs) ──────
    if (resumeRecord) {
      init = resumeRecord.init;
      for (const partNumber of resumeRecord.doneParts) {
        const etag = resumeRecord.etags[String(partNumber)];
        if (etag) {
          doneParts.add(partNumber);
          etagByPart.set(partNumber, etag);
        }
      }
      const skipped = doneParts.size;
      onEvent({
        type: "resumed",
        attachmentId: init.attachmentId,
        contentUri: init.contentUrl,
        access: resumeRecord.access,
        partsDone: skipped,
        skipped,
        note: `Resumed — reusing stored upload URLs, skipping ${skipped} already-uploaded part${skipped === 1 ? "" : "s"}.`,
      });
    } else {
      onEvent({ type: "phase", phase: "init" });
      const config = await authConfig(deps, access);
      const [item] = await initUploads(
        config,
        [
          {
            fileName: file.name,
            parts: computePartCount(file.size),
            contentType: file.type || undefined,
          },
        ],
        access,
      );
      if (!item) throw new ApiError("init returned no item", 0, "");
      init = item;
      onEvent({
        type: "init",
        attachmentId: item.attachmentId,
        contentUri: item.contentUrl,
        access,
      });
      // Persist the full init so a later re-pick can resume.
      saveResumeRecord({
        fingerprint,
        access,
        init: item,
        doneParts: [],
        etags: {},
        createdAt: Date.now(),
      });
      deps.onStoreChange();
    }

    // ── the draft transcript lands BEFORE the bytes (see RunUploadArgs) ────────
    if (transcriptDraft?.text.trim()) {
      throwIfCancelled(signal);
      const stored = await postTranscriptDraft(await authConfig(deps, access), {
        attachmentId: init.attachmentId,
        text: transcriptDraft.text,
        model: transcriptDraft.model,
      })
        .then((result) => result.stored)
        .catch(() => false);
      onEvent({ type: "draft", stored });
    }

    // ── PUT every not-yet-done part, a few at a time, each with bounded retry ──
    // Parts are independent S3 objects keyed by PartNumber, so order is free and a
    // bounded pool keeps a 200 MB file from paying 20 round-trips one after another.
    throwIfCancelled(signal);
    onEvent({ type: "phase", phase: "uploading" });
    let partsDone = doneParts.size;
    const pending = init.parts.filter(
      (part) => !doneParts.has(part.PartNumber),
    );
    const bytes = createByteReporter(
      Math.min(file.size, doneParts.size * PART_SIZE_BYTES),
      onEvent,
    );
    // One failing part stops the others at their next boundary instead of letting
    // them finish uploading bytes the final can never use.
    const pool = new AbortController();
    const poolSignal = signal ? anySignal([signal, pool.signal]) : pool.signal;
    let cursor = 0;
    const worker = async (): Promise<void> => {
      while (cursor < pending.length) {
        const part = pending[cursor++];
        if (pool.signal.aborted) return;
        throwIfCancelled(signal);
        const start = (part.PartNumber - 1) * PART_SIZE_BYTES;
        const end = Math.min(start + PART_SIZE_BYTES, file.size);
        const { etag } = await putPartWithRetry(
          part.signedUrl,
          file.slice(start, end),
          (attempt, error) => {
            onEvent({
              type: "retry",
              note: `part ${part.PartNumber}: attempt ${attempt} of ${PART_MAX_ATTEMPTS} failed (${error.message}) — retrying…`,
            });
          },
          poolSignal,
          (loaded) => bytes.report(part.PartNumber, loaded),
        );
        etagByPart.set(part.PartNumber, etag);
        doneParts.add(part.PartNumber);
        partsDone += 1;
        bytes.settle(part.PartNumber, end - start);
        markPartDone(fingerprint, part.PartNumber, etag);
        deps.onStoreChange();
        onEvent({ type: "progress", partsDone });
      }
    };
    try {
      await Promise.all(
        Array.from({ length: Math.min(PART_CONCURRENCY, pending.length) }, () =>
          worker(),
        ),
      );
    } catch (err) {
      pool.abort();
      throw err;
    }
    throwIfCancelled(signal);

    // Assemble the final part list from init order (done + freshly uploaded).
    const uploadedParts = init.parts.map((part) => {
      const etag = etagByPart.get(part.PartNumber);
      if (!etag) {
        throw new ApiError(`Missing ETag for part ${part.PartNumber}`, 0, "");
      }
      return { PartNumber: String(part.PartNumber), ETag: etag };
    });

    // ── final ─────────────────────────────────────────────────────────────────
    onEvent({ type: "phase", phase: "finalizing" });
    const finalConfig = await authConfig(deps, access);
    const result = await finalUpload(
      finalConfig,
      {
        fileKey: init.fileKey,
        fileId: init.fileId,
        attachmentId: init.attachmentId,
        parts: uploadedParts,
        ...(runAiAnalysis === false ? { runAiAnalysis: false } : {}),
      },
      access,
    );
    clearResumeRecord(fingerprint);
    deps.onStoreChange();
    onEvent({
      type: "final",
      contentUri: result.contentUrl,
      thumbnailUri: result.thumbnailUrl,
    });
    // Terminal status now arrives over the realtime relay: the hook is
    // subscribed to `attachment.processed` on this attachmentId's channel
    // (subscription starts at init, before processing can begin).
  } catch (err) {
    if (err instanceof UploadCancelledError || signal?.aborted) {
      clearResumeRecord(fingerprint);
      deps.onStoreChange();
      onEvent({ type: "error", error: "Upload cancelled" });
      return;
    }
    const apiError = err instanceof ApiError ? err : null;
    onEvent({
      type: "error",
      error: apiError
        ? apiError.message
        : `Unexpected error — ${(err as Error).message}`,
    });
    onEvent({
      type: "note",
      note: "Progress is saved — re-pick this same file to resume from where it stopped.",
    });
  }
}
